import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Users, Send, Paperclip, X, Clock, CalendarClock, Trash2, FileText, Image as ImageIcon } from 'lucide-react';

const EmailWriters = () => {
  const [writers, setWriters] = useState([]);
  const [counts, setCounts] = useState({ all: 0, published: 0, outstanding: 0 });
  const [loadingWriters, setLoadingWriters] = useState(true);
  const [recipient, setRecipient] = useState('all');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduled, setScheduled] = useState({ pending: [], history: [] });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchWriters();
    fetchScheduled();
  }, []);

  const fetchWriters = async () => {
    setLoadingWriters(true);
    try {
      const res = await axios.get(`${API}/admin/writers-list`);
      setWriters(res.data?.writers || []);
      setCounts(res.data?.counts || { all: 0, published: 0, outstanding: 0 });
    } catch (error) {
      console.error('Kon nie skrywers laai nie:', error);
      toast.error('Kon nie die skrywerslys laai nie.');
    } finally {
      setLoadingWriters(false);
    }
  };

  const fetchScheduled = async () => {
    try {
      const res = await axios.get(`${API}/admin/scheduled-writer-emails`);
      setScheduled({ pending: res.data?.pending || [], history: res.data?.history || [] });
    } catch (error) {
      console.debug('Kon nie geskeduleerde e-posse laai nie:', error);
    }
  };

  const selectedWriter = writers.find((w) => w.id === recipient);
  const recipientLabel = (() => {
    if (recipient === 'all') return `Alle Skrywers (${counts.all})`;
    if (recipient === 'published') return `Skrywers met gepubliseerde boeke (${counts.published})`;
    if (recipient === 'outstanding') return `Skrywers met uitstaande terugstuurings (${counts.outstanding})`;
    return selectedWriter ? selectedWriter.name : '';
  })();

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Aanhangsel is te groot. Maksimum 10 MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/email-writers/upload-attachment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [...prev, res.data]);
      toast.success('Aanhangsel bygevoeg.');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Kon nie aanhangsel oplaai nie.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    if (!subject.trim()) { toast.error('Voer asseblief \'n onderwerp in.'); return false; }
    if (!message.trim()) { toast.error('Voer asseblief \'n boodskap in.'); return false; }
    if (scheduleMode) {
      if (!scheduledFor) { toast.error('Kies asseblief \'n datum en tyd.'); return false; }
      if (new Date(scheduledFor).getTime() <= Date.now() + 60000) {
        toast.error('Kies asseblief \'n tyd in die toekoms.');
        return false;
      }
    }
    return true;
  };

  const openConfirm = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const body = {
        recipient,
        subject: subject.trim(),
        message: message.trim(),
        attachments: attachments.map((a) => ({ url: a.url, filename: a.filename, size_bytes: a.size_bytes })),
      };
      if (scheduleMode && scheduledFor) {
        body.scheduled_for = new Date(scheduledFor).toISOString();
      }
      const res = await axios.post(`${API}/admin/email-writers`, body);
      if (res.data?.scheduled) {
        toast.success('E-pos geskeduleer.');
        fetchScheduled();
      } else {
        const count = res.data?.sent_count ?? 0;
        const failed = res.data?.failed_count ?? 0;
        toast.success(res.data?.mode === 'individual'
          ? `E-pos privaat gestuur aan ${count} skrywer(s)${failed ? `, ${failed} het misluk` : ''}.`
          : 'E-pos suksesvol gestuur.');
      }
      setSubject('');
      setMessage('');
      setAttachments([]);
      setScheduleMode(false);
      setScheduledFor('');
      setConfirmOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Kon nie die e-pos stuur nie.';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.post(`${API}/admin/scheduled-writer-emails/${id}/cancel`);
      toast.success('Geskeduleerde e-pos gekanselleer.');
      fetchScheduled();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Kon nie kanselleer nie.';
      toast.error(detail);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('af-ZA', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const recipientText = (r) => {
    if (r === 'all') return 'Alle skrywers';
    if (r === 'published') return 'Gepubliseerde skrywers';
    if (r === 'outstanding') return 'Uitstaande terugstuurings';
    const w = writers.find((x) => x.id === r);
    return w ? w.name : 'Een skrywer';
  };

  return (
    <div className="space-y-6" data-testid="email-writers-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <Mail className="h-6 w-6 text-orange-500" />
          E-pos Skrywers
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Stuur versoeke, opdaterings of nuus regstreeks aan jou skrywers. Kies een skrywer of 'n groep — wanneer jy aan 'n groep stuur, ontvang elke skrywer die e-pos privaat en sien niemand die ander se e-pos adresse nie. Jou handtekening word outomaties onderaan elke e-pos gevoeg.
        </p>
      </div>

      <Card className="border-orange-200">
        <CardHeader>
          <CardTitle className="text-lg">Nuwe Boodskap</CardTitle>
          <CardDescription>Die boodskap word namens Die Biblioteek gestuur.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="recipient-select">Ontvanger</Label>
            {loadingWriters ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Laai skrywers...
              </div>
            ) : (
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger id="recipient-select" data-testid="email-recipient-select" className="w-full">
                  <SelectValue placeholder="Kies ontvanger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="email-recipient-all">
                    <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Alle Skrywers ({counts.all})</span>
                  </SelectItem>
                  <SelectItem value="published" data-testid="email-recipient-published">
                    Skrywers met gepubliseerde boeke ({counts.published})
                  </SelectItem>
                  <SelectItem value="outstanding" data-testid="email-recipient-outstanding">
                    Skrywers met uitstaande terugstuurings ({counts.outstanding})
                  </SelectItem>
                  {writers.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`email-recipient-${w.id}`}>
                      {w.name} — {w.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(recipient === 'all' || recipient === 'published' || recipient === 'outstanding') && (
              <p className="text-xs text-orange-600">
                Elke skrywer in die gekose groep ontvang die e-pos privaat (afsonderlik gestuur).
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject-input">Onderwerp</Label>
            <Input
              id="subject-input"
              data-testid="email-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Bv. Belangrike opdatering vir skrywers"
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message-input">Boodskap</Label>
            <Textarea
              id="message-input"
              data-testid="email-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Skryf jou boodskap hier..."
              rows={10}
              maxLength={10000}
            />
            <p className="text-xs text-gray-400 text-right">{message.length}/10000</p>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label>Aanhangsels (PDF of prent, opsioneel)</Label>
            <div className="flex flex-wrap gap-2">
              {attachments.map((a, idx) => (
                <div key={idx} data-testid={`email-attachment-${idx}`} className="flex items-center gap-2 bg-gray-100 border rounded-md px-3 py-1.5 text-sm">
                  {(/\.pdf$/i.test(a.filename)) ? <FileText className="h-4 w-4 text-red-500" /> : <ImageIcon className="h-4 w-4 text-blue-500" />}
                  <span className="max-w-[180px] truncate">{a.filename}</span>
                  <button type="button" onClick={() => removeAttachment(idx)} data-testid={`email-attachment-remove-${idx}`} className="text-gray-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              data-testid="email-attachment-input"
              onChange={handleFilePick}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="email-attachment-btn"
              className="gap-2"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              Voeg aanhangsel by
            </Button>
          </div>

          {/* Scheduling */}
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="schedule-toggle"
                data-testid="email-schedule-toggle"
                checked={scheduleMode}
                onChange={(e) => setScheduleMode(e.target.checked)}
                className="h-4 w-4 accent-orange-500"
              />
              <Label htmlFor="schedule-toggle" className="cursor-pointer flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-purple-500" /> Skeduleer vir later
              </Label>
            </div>
            {scheduleMode && (
              <Input
                type="datetime-local"
                data-testid="email-schedule-datetime"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="w-full sm:w-72"
              />
            )}
          </div>

          <div className="flex justify-end">
            <Button
              data-testid="email-send-btn"
              onClick={openConfirm}
              disabled={loadingWriters || uploading}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white gap-2"
            >
              {scheduleMode ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {scheduleMode ? 'Skeduleer E-pos' : 'Stuur E-pos'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled emails list */}
      {(scheduled.pending.length > 0 || scheduled.history.length > 0) && (
        <Card className="border-purple-200" data-testid="scheduled-emails-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" /> Geskeduleerde E-posse
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {scheduled.pending.map((s) => (
              <div key={s.id} data-testid={`scheduled-email-${s.id}`} className="flex items-start justify-between gap-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-500">Hangende</Badge>
                    <span className="text-sm font-medium truncate">{s.subject}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {recipientText(s.recipient)} · {fmtDate(s.scheduled_for)}
                    {s.attachments?.length ? ` · ${s.attachments.length} aanhangsel(s)` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost" size="sm"
                  data-testid={`scheduled-cancel-${s.id}`}
                  onClick={() => cancelScheduled(s.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1 shrink-0"
                >
                  <Trash2 className="h-4 w-4" /> Kanselleer
                </Button>
              </div>
            ))}
            {scheduled.history.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-3 bg-gray-50 border rounded-lg p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={s.status === 'sent' ? 'bg-green-500' : s.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'}>
                      {s.status === 'sent' ? 'Gestuur' : s.status === 'failed' ? 'Misluk' : 'Gekanselleer'}
                    </Badge>
                    <span className="text-sm font-medium truncate">{s.subject}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {recipientText(s.recipient)} · {fmtDate(s.scheduled_for)}
                    {s.status === 'sent' && s.sent_count != null ? ` · ${s.sent_count} ontvanger(s)` : ''}
                    {s.status === 'failed' && s.error ? ` · ${s.error}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !sending && setConfirmOpen(o)}>
        <DialogContent data-testid="email-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{scheduleMode ? 'Bevestig Skedulering' : 'Bevestig Versending'}</DialogTitle>
            <DialogDescription>
              {scheduleMode
                ? <>Hierdie e-pos word geskeduleer vir <strong>{fmtDate(scheduledFor ? new Date(scheduledFor).toISOString() : '')}</strong> aan <strong>{recipientLabel}</strong>.</>
                : <>Is jy seker jy wil hierdie e-pos stuur aan <strong>{recipientLabel}</strong>?</>}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1 border">
            <p><strong>Onderwerp:</strong> {subject}</p>
            <p className="whitespace-pre-wrap"><strong>Boodskap:</strong> {message}</p>
            {attachments.length > 0 && (
              <p><strong>Aanhangsels:</strong> {attachments.map((a) => a.filename).join(', ')}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid="email-cancel-btn">
              Kanselleer
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              data-testid="email-confirm-send-btn"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white gap-2"
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Verwerk...</>
                : (scheduleMode ? <><CalendarClock className="h-4 w-4" /> Ja, skeduleer</> : <><Send className="h-4 w-4" /> Ja, stuur nou</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailWriters;
